/**
 * Qualifier List Component
 * Displays a table of qualifiers with sorting and actions
 */

import { useState } from 'react';
import { FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaSortUp, FaSortDown } from 'react-icons/fa';
import { formatDateString } from '../../utils/dateUtils';
import styles from './QualifierList.module.css';

const QualifierList = ({ qualifiers, loading, error, onEdit, onDelete, onToggleActive }) => {
    const [sortField, setSortField] = useState('qualifierCode');
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    const sortedQualifiers = [...qualifiers].sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // String comparison
        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const handleDelete = async (qualifier) => {
        if (window.confirm(
            `Are you sure you want to delete qualifier "${qualifier.qualifierCode}"?\n\n` +
            'Note: Consider deactivating instead if this qualifier has been used in historical records.'
        )) {
            try {
                await onDelete(qualifier.qualifierId);
            } catch (err) {
                // Error handling is done in the hook
            }
        }
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading qualifiers...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.errorMessage}>Error: {error}</p>
            </div>
        );
    }

    if (qualifiers.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>No qualifiers found. Create your first qualifier to get started.</p>
            </div>
        );
    }

    return (
        <div className={styles.qualifierList}>
            <div className={styles.tableContainer}>
                <table className={styles.qualifierTable}>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('qualifierCode')} className={styles.sortable}>
                                Code {getSortIcon('qualifierCode')}
                            </th>
                            <th onClick={() => handleSort('description')} className={styles.sortable}>
                                Description {getSortIcon('description')}
                            </th>
                            <th onClick={() => handleSort('active')} className={styles.sortable}>
                                Status {getSortIcon('active')}
                            </th>
                            <th onClick={() => handleSort('lastMod')} className={styles.sortable}>
                                Last Modified {getSortIcon('lastMod')}
                            </th>
                            <th className={styles.actionsHeader}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedQualifiers.map((qualifier) => (
                            <tr key={qualifier.qualifierId} className={!qualifier.active ? styles.inactive : ''}>
                                <td className={styles.codeCell}>
                                    <strong>{qualifier.qualifierCode}</strong>
                                </td>
                                <td className={styles.descriptionCell}>
                                    {qualifier.description || <span className={styles.noValue}>-</span>}
                                </td>
                                <td>
                                    <span className={`${styles.statusBadge} ${qualifier.active ? styles.statusActive : styles.statusInactive}`}>
                                        {qualifier.active ? (
                                            <>
                                                <FaCheckCircle /> Active
                                            </>
                                        ) : (
                                            <>
                                                <FaTimesCircle /> Inactive
                                            </>
                                        )}
                                    </span>
                                </td>
                                <td className={styles.dateCell}>
                                    {formatDateString(qualifier.lastMod)}
                                </td>
                                <td className={styles.actions}>
                                    <button
                                        className={styles.editButton}
                                        onClick={() => onEdit(qualifier)}
                                        title="Edit qualifier"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => handleDelete(qualifier)}
                                        title="Delete qualifier"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.tableFooter}>
                <p>Total: {qualifiers.length} {qualifiers.length === 1 ? 'qualifier' : 'qualifiers'}</p>
            </div>
        </div>
    );
};

export default QualifierList;
